import axios from "axios";
import { isDeptHead, isHead } from "../utils/roles";

// Central API layer for the ticket work-timer / timesheet / live-activity
// feature. All endpoints live under `${API_BASE_URL}/api/ticket/timer/*`.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const timerUrl = (path) => `${API_BASE_URL}/api/ticket/timer${path}`;

export const timerService = {
  // Start a work session on a ticket. If the user already has a timer running
  // on another ticket, the backend auto-stops it (one timer per user).
  //
  // A ticket whose time budget is already spent comes back refused, with
  // data.budget_exhausted, so the app can ask before insisting. Re-send with
  // acknowledge_budget to start it anyway.
  async start({ ticket_id, user_id, acknowledge_budget }) {
    const res = await axios.post(timerUrl("/start"), {
      ticket_id,
      user_id,
      acknowledge_budget,
    });
    return res?.data;
  },

  // Stop the user's currently running session.
  async stop({ user_id }) {
    const res = await axios.post(timerUrl("/stop"), { user_id });
    return res?.data;
  },

  // The user's live session (or null) + server_time — used to resume the
  // ticking clock after an app relaunch / screen re-mount.
  async active(user_id) {
    const res = await axios.get(timerUrl(`/active/${user_id}`));
    return res?.data;
  },

  // A user's own timesheet: finished sessions grouped by day + ticket.
  async userTimesheet(user_id, { from, to } = {}) {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const res = await axios.get(timerUrl(`/timesheet/${user_id}`), { params });
    return res?.data;
  },

  // Team timesheet for a store (managers): grouped by user + ticket.
  async storeTimesheet(store_id, { date, dept_id } = {}) {
    const params = {};
    if (date) params.date = date;
    if (dept_id) params.dept_id = dept_id;
    const res = await axios.get(timerUrl(`/timesheet/store/${store_id}`), {
      params,
    });
    return res?.data;
  },

  // Live dashboard: everyone currently working in a store (running timers).
  async live(store_id, { dept_id } = {}) {
    const params = {};
    if (dept_id) params.dept_id = dept_id;
    const res = await axios.get(timerUrl(`/live/${store_id}`), { params });
    return res?.data;
  },

  // Stop SOMEBODY ELSE's running timer — a Head clearing a forgotten clock
  // from the Live Activity board.
  //
  // Not /stop with their user_id, even though that would have worked: this
  // records stop_reason='force_stopped', so a head's intervention stays
  // distinguishable in the timesheet from the person stopping their own work.
  // Pass session_id (the board has it), or user_id, or ticket_id.
  async forceStop({ requested_by, session_id, user_id, ticket_id }) {
    const res = await axios.post(timerUrl("/force-stop"), {
      requested_by,
      session_id,
      user_id,
      ticket_id,
    });
    return res?.data;
  },
};

// ── shared helpers ─────────────────────────────────────────────────────────

// Seconds -> "HH:MM:SS" (live clock)
export const formatClock = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(h)}:${p(m)}:${p(sec)}`;
};

// Seconds -> a compact human duration, e.g. "2h 15m", "45m", "30s"
export const formatDuration = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};

// Whether a role can view team-wide timesheets / the live dashboard.
//
// This used to be /head|owner|admin/i.test(role), matching on the SPELLING of
// the old role strings: 'servicehead', 'servicedepthead', 'storehead' and
// 'storedepthead' contained "head" and passed, 'servicetstaff' and 'storeuser'
// did not. The four roles it let through are exactly the heads and the HODs, so
// that is what it asks for now.
//
// The regex had to go, and not just for tidiness. Roles are role_master ids
// since backend migration 018, and /head/i.test(4) is false — every manager
// would have quietly lost the Timesheet and Live Activity screens, and the
// sidebar's managerOnly items with them, with nothing in the console to say so.
export const isManagerRole = (role) => isHead(role) || isDeptHead(role);

// Who may stop another person's timer from the live board.
//
// Narrower than isManagerRole on purpose: a HOD reads the board to see what
// their department is doing, but ending somebody's session edits their
// timesheet, and that is the Head's call. The backend checks the same two
// roles — this only decides whether the button is drawn.
export const canForceStopTimer = (role) => isHead(role);
